import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectLayout, THEME } from './store/layout/layoutSlice';
import { IdToken, useAuth0, User } from '@auth0/auth0-react';
import { Routes } from './routes/Routes';
import { authService } from './services/auth/authService';
import { setUser } from './store/user/userSlice';
import { setCookie } from './utilities/cookie';
import { COOKIES } from './constants/cookies';
import LoaderCircle from './components/Loading/circle/circle';
import { useEnhancedWhiteLabel } from './whiteLabel/hooks';

const AppContent = () => {
  useEnhancedWhiteLabel();

  const { getAccessTokenSilently, getIdTokenClaims, user, isLoading } =
    useAuth0();
  const layout = useSelector(selectLayout);
  const dispatch = useDispatch();

  useEffect(() => {
    authService.setGetTokenFn(() => getAccessTokenSilently());
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const isDark = document.documentElement.classList.contains('dark');
    if (layout.theme === THEME.DARK && !isDark) {
      document.documentElement.classList.add('dark');
    } else if (layout.theme !== THEME.DARK && isDark) {
      document.documentElement.classList.remove('dark');
    }
  }, [layout.theme]);

  const mapAuth0UserToUserData = (
    user: User | undefined,
    accessToken: string,
    idTokenClaims: IdToken | undefined
  ) => ({
    email: user?.email,
    accessToken,
    idToken: idTokenClaims?.__raw || '',
    name: user?.name,
    emailVerified: user?.email_verified,
    nickname: user?.nickname,
    picture: user?.picture,
    sub: user?.sub,
    updatedAt: user?.updated_at,
  });

  const initializeUser = useCallback(async () => {
    try {
      if (!user) return;

      const accessToken = await getAccessTokenSilently();
      if (!accessToken) return;

      const idTokenClaims = await getIdTokenClaims();

      const userData = mapAuth0UserToUserData(user, accessToken, idTokenClaims);
      dispatch(setUser(userData));
      setCookie(
        COOKIES.LOGGED_IN.NAME,
        'true',
        COOKIES.LOGGED_IN.DURATION_SECONDS
      );
    } catch (err) {
      console.warn('Failed to sync user:', err);
      dispatch(setUser(null));
    }
  }, [user, getAccessTokenSilently, getIdTokenClaims, dispatch]);

  useEffect(() => {
    if (!isLoading) initializeUser();
  }, [isLoading, initializeUser]);

  if (isLoading) {
    return <LoaderCircle />;
  }
  return <Routes />;
};

export default AppContent;
